import type { ReactNode } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography
} from '@mui/material';

import { spacing } from '@/constants/ui';
import { Z_INDEX } from '@/constants/z-index';
import type { ButtonProps } from '@/shared/ui/primitives/Button';
import Button from '@/shared/ui/primitives/Button';
import { normalizeSx } from '@/utils/normalizeSx';

import type { Sx } from '@/types/helpers';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  buttons?: ReactNode;
  closeIcon?: boolean;
  disableBackdropClick?: boolean;
  sx?: Sx;
  contentSx?: Sx;
  transitionDuration?: number | { appear?: number; enter?: number; exit?: number };
};

const dialogSx: Sx = { zIndex: Z_INDEX.dialog };

const paperSx: Sx = {
  backgroundColor: 'gx.mainBackground'
};

const backdropSx: Sx = {
  backgroundColor: 'rgba(18, 22, 63, 0.80)'
};

const contentSxBase: Sx = {
  minWidth: 590,
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between'
};

const headerSx: Sx = {
  display: 'flex',
  gap: 2,
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: spacing[1.5]
};

const closeButtonSx: Sx = { p: 0, marginLeft: 'auto' };

const actionsSx: Sx = { mt: spacing[2] };

const dialogButtonSx: Sx = {
  minWidth: '120px',
  height: '33px'
};

export const Dialog = ({
  open,
  onClose,
  title,
  children,
  buttons,
  closeIcon,
  disableBackdropClick,
  sx,
  contentSx,
  transitionDuration
}: DialogProps) => (
  <MuiDialog
    open={open}
    slotProps={{
      paper: {
        sx: [paperSx, ...normalizeSx(sx)]
      },
      backdrop: {
        sx: backdropSx
      }
    }}
    sx={dialogSx}
    transitionDuration={transitionDuration}
    onClose={(_, reason) => {
      if (disableBackdropClick && reason === 'backdropClick') return;
      onClose();
    }}
  >
    <DialogContent sx={[contentSxBase, ...normalizeSx(contentSx)]}>
      {(title || closeIcon) && (
        <Box sx={headerSx}>
          {title && <Typography variant='subtitle2'>{title}</Typography>}
          {closeIcon && (
            <IconButton
              sx={closeButtonSx}
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      )}
      {children}
      {buttons && <DialogActions sx={actionsSx}>{buttons}</DialogActions>}
    </DialogContent>
  </MuiDialog>
);

export const DialogButton = ({
  onClick,
  children,
  variant,
  ...rest
}: Pick<ButtonProps, 'variant' | 'children' | 'onClick' | 'disabled'>) => (
  <Button
    size='small'
    sx={dialogButtonSx}
    variant={variant}
    onClick={onClick}
    {...rest}
  >
    {children}
  </Button>
);

export const DialogContentText = ({ children }: { children: ReactNode }) => (
  <Typography variant='body1'>{children}</Typography>
);
